import type { InputConfig } from "./config";
import type { InputSample, GatherStrokeAction, InputListener } from "./types";
import { MAX_GATHER_RATE } from "./types";

interface TouchState {
  lastX: number;
  startX: number;
  hasStroke: boolean; // tracks if this touch made a valid stroke
  lastDirection: "left" | "right" | null; // track direction for reversals
  distanceInCurrentDirection: number; // accumulated distance in current direction
}

export class GatherDetector {
  private active = false;
  private touches = new Map<number, TouchState>();
  private listener: InputListener | null = null;
  private lastEmitTimes: number[] = [];
  private nowTick: () => number;
  private config: InputConfig;

  constructor(config: InputConfig, nowTick: () => number) {
    this.config = config;
    this.nowTick = nowTick;
  }

  setActive(active: boolean): void {
    this.active = active;
    if (!active) {
      this.touches.clear();
    }
  }

  onInput(listener: InputListener): void {
    this.listener = listener;
  }

  processSample(sample: InputSample): void {
    if (!this.active) return;

    const { pointerId, phase, x } = sample;

    if (phase === "start") {
      this.touches.set(pointerId, {
        lastX: x,
        startX: x,
        hasStroke: false,
        lastDirection: null,
        distanceInCurrentDirection: 0,
      });
    } else if (phase === "move") {
      const state = this.touches.get(pointerId);
      if (!state) return;

      const dx = x - state.lastX;
      const totalDx = Math.abs(x - state.startX);

      // Determine current direction
      const currentDirection: "left" | "right" | null =
        dx < -0.5 ? "left" : dx > 0.5 ? "right" : state.lastDirection;

      // Check for direction reversal
      if (
        state.lastDirection &&
        currentDirection &&
        currentDirection !== state.lastDirection
      ) {
        // Direction changed - check if we moved far enough to count as a reversal stroke
        if (state.distanceInCurrentDirection >= this.config.gatherMinStrokePx) {
          this.emitStroke(sample.t);
        }
        // Reset distance for new direction
        state.distanceInCurrentDirection = Math.abs(dx);
      } else if (currentDirection) {
        // Same direction - accumulate distance
        state.distanceInCurrentDirection += Math.abs(dx);
      }

      // Initial stroke detection (separate swipe from start)
      if (!state.hasStroke && totalDx >= this.config.gatherMinStrokePx) {
        state.hasStroke = true;
        this.emitStroke(sample.t);
      }

      state.lastX = x;
      state.lastDirection = currentDirection;
    } else if (phase === "end" || phase === "cancel") {
      this.touches.delete(pointerId);
    }
  }

  private emitStroke(time: number): void {
    // Apply rate cap
    const now = time / 1000;
    this.lastEmitTimes = this.lastEmitTimes.filter((t) => now - t < 1.0);

    if (this.lastEmitTimes.length >= MAX_GATHER_RATE) {
      return; // Drop event due to rate cap
    }

    this.lastEmitTimes.push(now);

    if (this.listener) {
      const action: GatherStrokeAction = {
        type: "gatherStroke",
        speed: this.lastEmitTimes.length,
        tick: this.nowTick(),
      };
      this.listener(action);
    }
  }
}
